import React, { useEffect, useState, useCallback, useRef, useMemo } from "react";
import {
  FaSearch, FaDownload, FaSyncAlt, FaMoneyCheckAlt,
  FaCheckCircle, FaTimesCircle, FaEdit, FaPlus, FaTrash,
  FaTruck, FaTruckMoving, FaSpinner, FaExclamationTriangle,
  FaUser, FaSave, FaHistory, FaFilter, FaUndo,
} from "react-icons/fa";
import { useNavigate } from "react-router-dom";

const API_BASE = process.env.REACT_APP_API_URL || "http://localhost:5000";

// ---- TYPES ----
type PaymentMode = "online" | "cash" | "bank" | "partial";
type SaveState   = "idle" | "saving" | "saved" | "error";

interface PaymentRecord { amount: number; paymentMode: PaymentMode; date: string; }
interface InvoiceUserPayment {
  userType: string; userName: string; amount: number;
  commission?: number; tds?: number; collection?: number; otherDeduction?: number;
  otherDeductionName?: string; payableAmount?: number;
  paid: boolean; paymentRecords: PaymentRecord[];
}
interface InvoiceItem {
  id: number; product: string; hsn: string; uom: string;
  qty: number; rate: number; amount: number;
}
interface Invoice {
  id: string; lrNumber: string; invoiceNumber: string;
  company: string; companyAddress: string; companyGst: string;
  companyPan: string; companyEmail: string; date: string;
  sender: string; senderAddress: string;
  receiver: string; receiverAddress: string;
  broker: string; brokerAddress: string;
  transporter: string; driver: string;
  route: string; origin: string; destination: string; vehicle: string;
  paymentType: string; paymentMode: PaymentMode;
  subtotal: number; gst: number; payment: number;
  items: InvoiceItem[]; profit: number;
  userDistributions: InvoiceUserPayment[];
  roundOff: number; amountInWords: string;
  status: "generated" | "partial" | "paid";
  pdfLink: string; subjects: string;
  saveState?: SaveState;
}

interface RawInvoiceRow {
  id: number;
  "InvoiceNo."?: string;
  sender?: number; company?: number; receiver?: number;
  broker?: number;
  transporter?: number; driver?: number; vehicles?: number;
  paymentPayableConsignor?: number;
  payment?: string;
  source?: string; destination?: string;
  paymentStatus?: string;
  senderAmountStatatus?: string;
  receiverAmountStatatus?: string;
  transporterAmountStatatus?: string;
  ItemsDescription?: string;
  items?: any[];
  EWAYBILLNO?: string; subjects?: string; link?: string;
  created_at?: string;
}

interface ResolvedUser {
  id: number; name: string; email: string; address: string;
  companyName: string; gst: string; pan: string; phone: string; role: string;
}

const companyCache = new Map<number, string>();
async function fetchCompanyName(id: number): Promise<string> {
  if (!id) return "—";
  if (companyCache.has(id)) return companyCache.get(id)!;
  try {
    const res = await fetch(`${API_BASE}/companyauth/company/${id}`, { credentials: "include" });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const data = await res.json();
    const name =
      data.company_name || data.companyName || data.name ||
      data.firm_name   || data.title        || `#${id}`;
    companyCache.set(id, name);
    return name;
  } catch {
    companyCache.set(id, `#${id}`);
    return `#${id}`;
  }
}
const vehicleCache = new Map<number, string>();
async function fetchVehicleNumber(id: number): Promise<string> {
  if (!id) return "—";
  if (vehicleCache.has(id)) return vehicleCache.get(id)!;
  try {
    const res = await fetch(`${API_BASE}/auth/vehicles/${id}`, { credentials: "include" });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const data = await res.json();
    const number =
      data.vehicle_number || data.vehicleNumber || data.number ||
      data.reg_number || data.registration_number || `#${id}`;
    vehicleCache.set(id, number);
    return number;
  } catch {
    vehicleCache.set(id, `#${id}`);
    return `#${id}`;
  }
}

const userCache = new Map<number, ResolvedUser>();
function normaliseUser(raw: Record<string, unknown>, id: number): ResolvedUser {
  const pick = (...keys: string[]): string => {
    for (const k of keys) {
      const v = raw[k];
      if (v && typeof v === "string" && v.trim()) return v.trim();
    }
    return "";
  };
  return {
    id,
    name:        pick("name", "full_name", "fullName", "username", "user_name", "firstName", "first_name", "displayName", "display_name"),
    email:       pick("email", "email_address", "emailAddress"),
    address:     pick("address", "full_address", "location", "city"),
    companyName: pick("company_name", "companyName", "company", "firm_name", "firmName", "business_name", "businessName", "organization", "name"),
    gst:         pick("gst", "gst_number", "gstNumber", "gstin", "GSTIN"),
    pan:         pick("pan", "pan_number", "panNumber", "pancard", "PAN"),
    phone:       pick("phone", "mobile", "contact", "phone_number", "phoneNumber"),
    role:        pick("role", "user_role", "userRole", "type"),
  };
}

async function fetchUser(id: number): Promise<ResolvedUser> {
  if (userCache.has(id)) return userCache.get(id)!;
  try {
    const res = await fetch(`${API_BASE}/auth/user/${id}`, { credentials: "include" });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const raw: Record<string, unknown> = await res.json();
    const user = normaliseUser(raw, id);
    userCache.set(id, user);
    return user;
  } catch {
    const stub: ResolvedUser = { id, name: `#${id}`, email:"", address:"", companyName:"", gst:"", pan:"", phone:"", role:"" };
    userCache.set(id, stub);
    return stub;
  }
}

async function fetchUsersBatch(ids: number[]): Promise<Map<number, ResolvedUser>> {
  const uncached = ids.filter((id) => !userCache.has(id));
  if (uncached.length > 0) {
    try {
      const res = await fetch(`${API_BASE}/auth/users/batch?ids=${uncached.join(",")}`, { credentials: "include" });
      if (res.ok) {
        const byId: Record<string, { name: string; address: string }> = await res.json();
        for (const idStr of Object.keys(byId)) {
          const id = Number(idStr);
          const raw = byId[idStr];
          userCache.set(id, {
            id, name: raw.name || `#${id}`, email: "", address: raw.address || "",
            companyName: "", gst: "", pan: "", phone: "", role: "",
          });
        }
      }
    } catch {}
    for (const id of uncached) {
      if (!userCache.has(id)) {
        userCache.set(id, { id, name: `#${id}`, email:"", address:"", companyName:"", gst:"", pan:"", phone:"", role:"" });
      }
    }
  }
  const result = new Map<number, ResolvedUser>();
  ids.forEach((id) => result.set(id, userCache.get(id)!));
  return result;
}

async function fetchDriverNamesBatch(ids: number[]): Promise<Map<number, string>> {
  const result = new Map<number, string>();
  if (ids.length === 0) return result;
  try {
    const res = await fetch(`${API_BASE}/auth/drivers/batch?ids=${ids.join(",")}`, { credentials: "include" });
    const byId: Record<string, string> = res.ok ? await res.json() : {};
    ids.forEach((id) => result.set(id, byId[id] || `#${id}`));
  } catch {
    ids.forEach((id) => result.set(id, `#${id}`));
  }
  return result;
}

async function fetchCompanyNamesBatch(ids: number[]): Promise<Map<number, string>> {
  const result = new Map<number, string>();
  if (ids.length === 0) return result;
  try {
    const res = await fetch(`${API_BASE}/companyauth/company/batch?ids=${ids.join(",")}`, { credentials: "include" });
    const byId: Record<string, string> = res.ok ? await res.json() : {};
    ids.forEach((id) => result.set(id, byId[id] || `#${id}`));
  } catch {
    ids.forEach((id) => result.set(id, `#${id}`));
  }
  return result;
}

async function fetchVehicleNumbersBatch(ids: number[]): Promise<Map<number, string>> {
  const result = new Map<number, string>();
  if (ids.length === 0) return result;
  try {
    const res = await fetch(`${API_BASE}/auth/vehicles/batch?ids=${ids.join(",")}`, { credentials: "include" });
    const byId: Record<string, string> = res.ok ? await res.json() : {};
    ids.forEach((id) => result.set(id, byId[id] || `#${id}`));
  } catch {
    ids.forEach((id) => result.set(id, `#${id}`));
  }
  return result;
}

async function fetchDriverName(driverId: number): Promise<string> {
  if (!driverId) return "—";
  try {
    const res = await fetch(`${API_BASE}/auth/drivers/${driverId}`, { credentials: "include" });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const data = await res.json();
    if (typeof data === "object") {
      for (const key of ["driverName", "name", "full_name"]) {
        if (typeof data[key] === "string" && data[key].trim()) return data[key].trim();
      }
    }
    return `#${driverId}`;
  } catch {
    return `#${driverId}`;
  }
}

async function resolveAllUsers(rows: RawInvoiceRow[]): Promise<{
  users: Map<number, ResolvedUser>;
  driverNames: Map<number, string>;
  companyNames: Map<number, string>;
  vehicleNumbers: Map<number, string>;
}> {
  const ids = new Set<number>();
  const driverIds = new Set<number>();
  const companyIds = new Set<number>();
  const vehicleIds = new Set<number>();

  for (const row of rows) {
    for (const f of [row.sender, row.receiver, row.broker, row.transporter, row.paymentPayableConsignor]) {
      if (typeof f === "number") ids.add(f);
    }
    if (typeof row.driver   === "number") driverIds.add(row.driver);
    if (typeof row.company  === "number") companyIds.add(row.company);
    if (typeof row.vehicles === "number") vehicleIds.add(row.vehicles);
  }

  const [users, driverNames, companyNames, vehicleNumbers] = await Promise.all([
    fetchUsersBatch([...ids]),
    fetchDriverNamesBatch([...driverIds]),
    fetchCompanyNamesBatch([...companyIds]),
    fetchVehicleNumbersBatch([...vehicleIds]),
  ]);
  return { users, driverNames, companyNames, vehicleNumbers };
}

// ---- HELPERS ----
function parseIndianCurrency(value: unknown): number {
  if (typeof value === "number") return value;
  if (typeof value === "string") {
    const cleaned = value
      .replace(/Rs\.?\s*/gi, "")
      .replace(/₹/g, "")
      .replace(/\/-/g, "")
      .replace(/[,\s]/g, "");
    const n = Number(cleaned);
    return isNaN(n) ? 0 : n;
  }
  return 0;
}

function getTodayLocalDate(): string {
  const d = new Date();
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function formatYMD(d: any): string {
  if (!d) return getTodayLocalDate();
  const str = String(d);
  const match = str.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (match) return `${match[1]}-${match[2]}-${match[3]}`;
  try {
    const dt = new Date(d);
    if (!isNaN(dt.getTime())) {
      const y = dt.getFullYear();
      const m = String(dt.getMonth() + 1).padStart(2, "0");
      const day = String(dt.getDate()).padStart(2, "0");
      return `${y}-${m}-${day}`;
    }
  } catch {}
  return str.slice(0, 10);
}

function fmtDate(d: any): string {
  if (!d) return "—";
  const str = String(d);
  const match = str.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (match) {
    const [, y, m, day] = match;
    const dateObj = new Date(Number(y), Number(m) - 1, Number(day));
    return dateObj.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
  }
  try {
    const dt = new Date(d);
    if (!isNaN(dt.getTime())) {
      return dt.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
    }
  } catch {}
  return str;
}

function displayName(u: ResolvedUser | undefined, fallbackId: number | undefined): string {
  if (!u) return fallbackId ? `#${fallbackId}` : "—";
  return u.companyName || u.name || u.email || `#${u.id}`;
}

function normalizePaymentMode(raw: string): PaymentMode {
  const l = raw.toLowerCase();
  if (l.includes("online")) return "online";
  if (l.includes("cash"))   return "cash";
  if (l.includes("partial")) return "partial";
  return "bank";
}

function deriveStatus(paymentStatus: string | undefined): Invoice["status"] {
  const s = (paymentStatus ?? "").toLowerCase();
  if (s === "paid" || s === "completed") return "paid";
  if (s === "partial") return "partial";
  return "generated";
}

function parseItemIds(raw: string | undefined): number[] {
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw);
    if (Array.isArray(parsed)) return parsed.filter((x) => typeof x === "number");
  } catch {}
  return [];
}

function safeToString(val: any): string {
  if (val == null) return "";
  if (typeof val === "object") {
    if (val.name) return String(val.name);
    if (val.companyName) return String(val.companyName);
    if (val.company_name) return String(val.company_name);
    if (val.title) return String(val.title);
    return "";
  }
  return String(val);
}

// ---- MAPPER ----
function mapRawToInvoice(
  raw: RawInvoiceRow,
  users: Map<number, ResolvedUser>,
  driverNames?: Map<number, string>,
  companyNames?: Map<number, string>,
  vehicleNumbers?: Map<number, string>
): Invoice {
  const companyName = raw.company && companyNames
    ? (companyNames.get(raw.company) ?? `#${raw.company}`)
    : "—";
  const sender      = users.get(raw.sender      ?? -1);
  const receiver    = users.get(raw.receiver    ?? -1);
  const broker      = users.get(raw.broker      ?? -1);
  const transporter = users.get(raw.transporter ?? -1);

  const total         = parseIndianCurrency(raw.payment);
  const invoiceNumber = raw["InvoiceNo."] ?? `INV-${raw.id}`;
  const origin        = raw.source?.trim()      || "—";
  const destination   = raw.destination?.trim() || "—";
  const pdfLink       = raw.link
    ? raw.link.startsWith("http") ? raw.link : `${API_BASE}${raw.link}`
    : "";

  let driverValue: string;
  if (typeof raw.driver === "number" && driverNames) {
    driverValue = safeToString(driverNames.get(raw.driver) || `#${raw.driver}`);
  } else if (typeof raw.driver === "number") {
    driverValue = `#${raw.driver}`;
  } else {
    driverValue = "—";
  }

  const vehicleValue = typeof raw.vehicles === "number" && vehicleNumbers
    ? safeToString(vehicleNumbers.get(raw.vehicles) ?? `#${raw.vehicles}`)
    : typeof raw.vehicles === "number"
      ? `#${raw.vehicles}`
      : "—";

  return {
    id: String(raw.id),
    lrNumber: invoiceNumber, invoiceNumber,
    company: companyName,
    companyAddress:  "",
    companyGst:     "",
    companyPan:     "",
    companyEmail:   "",
    date:           raw.created_at    ?? "",
    sender:         safeToString(displayName(sender, raw.sender)),
    senderAddress:  safeToString(sender?.address   || ""),
    receiver:       safeToString(displayName(receiver, raw.receiver)),
    receiverAddress:safeToString(receiver?.address || ""),
    broker:         safeToString(displayName(broker, raw.broker)),
    brokerAddress:  safeToString(broker?.address   || ""),
    transporter:    safeToString(displayName(transporter, raw.transporter)),
    driver:         driverValue,
    route:          origin !== "—" && destination !== "—" ? `${origin} → ${destination}` : "—",
    origin, destination,
    vehicle:        vehicleValue,
    paymentType:    raw.paymentStatus ?? "—",
    paymentMode:    normalizePaymentMode(raw.paymentStatus ?? ""),
    subtotal: total, gst: 0, payment: total, profit: 0,
    roundOff: 0, amountInWords: "",
    status:         deriveStatus(raw.paymentStatus),
    userDistributions: [],
    pdfLink,
    subjects: raw.subjects ?? "",
    saveState: "idle",
    items: Array.isArray(raw.items) && raw.items.length > 0
      ? raw.items.map((it: any, idx: number) => ({
          id: Number(it.id || idx + 1),
          product: safeToString(it.description || it.product || `Item #${it.id || idx + 1}`),
          hsn: safeToString(it.hsm_code || it.hsn || "—"),
          uom: safeToString(it.uom || "Ton"),
          qty: Number(it.qty || 0),
          rate: Number(it.rate || 0),
          amount: Number(it.amount || 0),
        }))
      : parseItemIds(raw.ItemsDescription).map((itemId) => ({
          id: itemId, product: `Item #${itemId}`,
          hsn: "—", uom: "—", qty: 0, rate: 0, amount: 0,
        })),
  };
}

function getNetPayable(u: InvoiceUserPayment): number {
  if (u.payableAmount != null) return u.payableAmount;
  const comm = u.commission || 0;
  const tds = u.tds || 0;
  const coll = u.collection || 0;
  const oth = u.otherDeduction || 0;
  return Math.max(0, u.amount - comm - tds - coll - oth);
}

// ---- SETTLEMENT DB API ----
async function loadSettlementFromDB(invoiceId: string): Promise<InvoiceUserPayment[] | null> {
  try {
    const res = await fetch(`${API_BASE}/settlment/${invoiceId}`, { credentials: "include" });
    if (!res.ok) return null;
    const data = await res.json();
    if (!data || !Array.isArray(data.distributions)) return null;
    return data.distributions.map((d: Record<string, unknown>) => {
      const grossAmt = Number(d.amount || 0);
      const comm = Number(d.commission || 0);
      const tds = Number(d.tds || 0);
      const coll = Number(d.collection || 0);
      const oth = Number(d.otherDeduction || d.other_deduction || 0);
      const othName = String(d.otherDeductionName || d.other_deduction_name || "");
      const netPayable = d.net_amount != null
        ? Number(d.net_amount)
        : Math.max(0, grossAmt - comm - tds - coll - oth);

      return {
        userType: String(d.user_type || ""),
        userName: String(d.user_name || ""),
        amount: grossAmt,
        commission: comm,
        tds: tds,
        collection: coll,
        otherDeduction: oth,
        otherDeductionName: othName,
        payableAmount: netPayable,
        paid: Boolean(d.paid),
        paymentRecords: Array.isArray(d.paymentRecords)
          ? (d.paymentRecords as Array<Record<string, unknown>>).map((r) => ({
              amount: Number(r.amount || 0),
              paymentMode: String(r.paymentMode || "bank") as PaymentMode,
              date: String(r.date || ""),
            }))
          : [],
      };
    });
  } catch {
    return null;
  }
}

async function saveSettlementToDB(invoice: Invoice): Promise<boolean> {
  try {
    const res = await fetch(`${API_BASE}/settlment/${invoice.id}`, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        invoiceNo:     invoice.invoiceNumber,
        totalAmount:   invoice.payment,
        distributions: invoice.userDistributions.map((d) => ({
          userType:           d.userType,
          userName:           d.userName,
          amount:             d.amount,
          commission:         d.commission || 0,
          tds:                d.tds || 0,
          collection:         d.collection || 0,
          otherDeduction:     d.otherDeduction || 0,
          otherDeductionName: d.otherDeductionName || "",
          payableAmount:      getNetPayable(d),
          paymentRecords:     d.paymentRecords,
        })),
      }),
    });
    return res.ok;
  } catch {
    return false;
  }
}

async function fetchAllInvoices(): Promise<Invoice[]> {
  const [invRes, bulkSettlRes] = await Promise.all([
    fetch(`${API_BASE}/invoiceapi/invoice`, { credentials: "include" }),
    fetch(`${API_BASE}/settlment/all-bulk`, { credentials: "include" }).catch(() => null),
  ]);

  if (!invRes.ok) throw new Error(`HTTP ${invRes.status}: ${invRes.statusText}`);
  const rows: RawInvoiceRow[] = await invRes.json();
  if (!Array.isArray(rows)) throw new Error("Unexpected response format");

  const bulkMap: Record<string, InvoiceUserPayment[]> = bulkSettlRes && bulkSettlRes.ok ? await bulkSettlRes.json() : {};

  const { users, driverNames, companyNames, vehicleNumbers } = await resolveAllUsers(rows);
  return rows.map((row) => {
    const inv = mapRawToInvoice(row, users, driverNames, companyNames, vehicleNumbers);
    if (bulkMap[inv.id] && bulkMap[inv.id].length > 0) {
      inv.userDistributions = bulkMap[inv.id];
      const allPaid = inv.userDistributions.every((d) => d.paid);
      const anyPaid = inv.userDistributions.some((d) => sumPaid(d) > 0);
      inv.status = allPaid ? "paid" : anyPaid ? "partial" : "generated";
    }
    return inv;
  });
}

// ---- UI HELPERS ----
const userTypeLabel: Record<string, string> = {
  sender:"Sender", receiver:"Receiver", broker:"Broker",
  transporter:"Transporter", consignor:"Consignor",
  gst:"GST", tds:"TDS", profit:"Profit", driver:"Driver",
  vehicle:"Vehicle",
};
const paymentModeLabel = (m: PaymentMode) =>
  ({ online:"Online", bank:"Bank", cash:"Cash", partial:"Partial" }[m]);

function statusColor(s: Invoice["status"]) {
  if (s === "paid")    return "text-green-800 bg-green-100";
  if (s === "partial") return "text-yellow-800 bg-yellow-100";
  return "text-blue-800 bg-blue-100";
}
function sumPaid(u: InvoiceUserPayment) {
  return u.paymentRecords.reduce((a, r) => a + (r.amount || 0), 0);
}
function sumAllocated(dists: InvoiceUserPayment[]) {
  return dists.reduce((a, d) => a + (d.amount || 0), 0);
}
const presetUserTypes = ["sender","receiver","broker","driver","vehicle","consignor","gst","tds","profit"];

interface InvoiceParticipant { userType: string; userName: string; }

function getInvoiceParticipants(invoice: Invoice): InvoiceParticipant[] {
  const participants: InvoiceParticipant[] = [];
  if (invoice.receiver && invoice.receiver !== "—") participants.push({ userType: "receiver", userName: safeToString(invoice.receiver) });
  if (invoice.broker   && invoice.broker   !== "—") participants.push({ userType: "broker",   userName: safeToString(invoice.broker) });
  if (invoice.driver   && invoice.driver   !== "—") participants.push({ userType: "driver",   userName: safeToString(invoice.driver) });
  if (invoice.vehicle  && invoice.vehicle  !== "—") participants.push({ userType: "vehicle",  userName: safeToString(invoice.vehicle) });
  return participants;
}

// ---- SAVE BUTTON ----
function SaveButton({ state, onClick }: { state: SaveState; onClick: () => void }) {
  if (state === "saving") return (
    <button disabled className="flex items-center gap-1 px-3 py-1.5 text-xs rounded-lg bg-blue-100 text-blue-600 font-bold border border-blue-200">
      <FaSpinner className="animate-spin" /> Saving…
    </button>
  );
  if (state === "saved") return (
    <button disabled className="flex items-center gap-1 px-3 py-1.5 text-xs rounded-lg bg-green-100 text-green-700 font-bold border border-green-200">
      <FaCheckCircle /> Saved
    </button>
  );
  if (state === "error") return (
    <button onClick={onClick} className="flex items-center gap-1 px-3 py-1.5 text-xs rounded-lg bg-red-100 text-red-700 font-bold border border-red-300 hover:bg-red-200">
      <FaExclamationTriangle /> Retry Save
    </button>
  );
  return (
    <button onClick={onClick} className="flex items-center gap-1 px-3 py-1.5 text-xs rounded-lg bg-blue-600 text-white font-bold border border-blue-700 hover:bg-blue-800 transition shadow">
      <FaSave /> Save to DB
    </button>
  );
}

// ---- COMPONENT ----
const Settlement: React.FC = () => {
  const navigate = useNavigate();

  const [invoices, setInvoices]     = useState<Invoice[]>([]);
  const [loading,  setLoading]      = useState(true);
  const [error,    setError]        = useState<string | null>(null);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [search,   setSearch]       = useState("");

  // Advanced Filters State
  const [showAdvanceFilters, setShowAdvanceFilters]   = useState(false);
  const [filterStatus, setFilterStatus]               = useState("");
  const [filterUserType, setFilterUserType]           = useState("");
  const [filterCompany, setFilterCompany]             = useState("");
  const [filterParty, setFilterParty]                 = useState("");
  const [filterVehicle, setFilterVehicle]             = useState("");
  const [filterDateFrom, setFilterDateFrom]           = useState("");
  const [filterDateTo, setFilterDateTo]               = useState("");
  const [filterDeductionType, setFilterDeductionType] = useState("");

  const activeFilterCount = [
    filterStatus,
    filterUserType,
    filterCompany,
    filterParty,
    filterVehicle,
    filterDateFrom,
    filterDateTo,
    filterDeductionType,
  ].filter(Boolean).length;

  const resetFilters = () => {
    setSearch("");
    setFilterStatus("");
    setFilterUserType("");
    setFilterCompany("");
    setFilterParty("");
    setFilterVehicle("");
    setFilterDateFrom("");
    setFilterDateTo("");
    setFilterDeductionType("");
  };

  const uniqueCompanies = useMemo(() => {
    const set = new Set<string>();
    invoices.forEach((inv) => {
      const c = safeToString(inv.company);
      if (c && c !== "—") set.add(c);
    });
    return Array.from(set).sort();
  }, [invoices]);

  const uniqueParties = useMemo(() => {
    const set = new Set<string>();
    invoices.forEach((inv) => {
      const sender = safeToString(inv.sender);
      if (sender && sender !== "—") set.add(sender);
      const receiver = safeToString(inv.receiver);
      if (receiver && receiver !== "—") set.add(receiver);
      const transporter = safeToString(inv.transporter);
      if (transporter && transporter !== "—") set.add(transporter);
      const driver = safeToString(inv.driver);
      if (driver && driver !== "—") set.add(driver);
      const broker = safeToString(inv.broker);
      if (broker && broker !== "—") set.add(broker);
      inv.userDistributions.forEach((d) => {
        const uName = safeToString(d.userName);
        if (uName && uName !== "—") set.add(uName);
      });
    });
    return Array.from(set).sort();
  }, [invoices]);

  const uniqueVehicles = useMemo(() => {
    const set = new Set<string>();
    invoices.forEach((inv) => {
      const v = safeToString(inv.vehicle);
      if (v && v !== "—") set.add(v);
    });
    return Array.from(set).sort();
  }, [invoices]);
  const [selectedUserTab, setSelectedUserTab] = useState<Record<string, number>>({});
  const [cardTab, setCardTab]                 = useState<Record<string, "payouts" | "details">>({});
  const [loadingSettlement, setLoadingSettlement] = useState<Record<string, boolean>>({});
  const [payModal, setPayModal] = useState<{
    invoiceId:string; userIdx:number; amt:string; mode:PaymentMode; date:string;
    full?:boolean; editing?:boolean; editingIdx?:number;
  } | null>(null);

  const [editDist, setEditDist] = useState<{
    invoiceId: string;
    editingIdx: number | null;
    source: "auto" | "manual";
    selectedParticipant: string;
    userType: string;
    userName: string;
    amount: string;
    commission: string;
    tds: string;
    collection: string;
    otherDeduction: string;
    otherDeductionName: string;
  } | null>(null);

  const saveTimers = useRef<Record<string, ReturnType<typeof setTimeout>>>({});

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try { setInvoices(await fetchAllInvoices()); }
    catch (e) { setError(e instanceof Error ? e.message : "Failed to load"); }
    finally   { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!expandedId) return;
    const invoice = invoices.find((i) => i.id === expandedId);
    if (!invoice || invoice.userDistributions.length > 0) return;
    setLoadingSettlement((p) => ({ ...p, [expandedId]: true }));
    loadSettlementFromDB(expandedId).then((dists) => {
      if (dists && dists.length > 0) {
        setInvoices((prev) => prev.map((inv) =>
          inv.id === expandedId ? { ...inv, userDistributions: dists } : inv
        ));
      }
      setLoadingSettlement((p) => ({ ...p, [expandedId]: false }));
    });
  }, [expandedId, invoices]);

  function scheduleAutoSave(invoiceId: string) {
    if (saveTimers.current[invoiceId]) clearTimeout(saveTimers.current[invoiceId]);
    saveTimers.current[invoiceId] = setTimeout(() => {
      triggerSave(invoiceId);
    }, 1500);
  }

  async function triggerSave(invoiceId: string) {
    setInvoices((prev) => prev.map((inv) =>
      inv.id === invoiceId ? { ...inv, saveState: "saving" } : inv
    ));
    const invoice = invoices.find((i) => i.id === invoiceId)
                 || (await new Promise<Invoice | undefined>((resolve) => {
                      setInvoices((prev) => { resolve(prev.find((i) => i.id === invoiceId)); return prev; });
                    }));
    if (!invoice) return;
    const ok = await saveSettlementToDB(invoice);
    setInvoices((prev) => prev.map((inv) =>
      inv.id === invoiceId ? { ...inv, saveState: ok ? "saved" : "error" } : inv
    ));
    setTimeout(() => {
      setInvoices((prev) => prev.map((inv) =>
        inv.id === invoiceId && inv.saveState === "saved" ? { ...inv, saveState: "idle" } : inv
      ));
    }, 3000);
  }

  async function triggerSaveLatest(invoiceId: string, latest: Invoice) {
    setInvoices((prev) => prev.map((inv) =>
      inv.id === invoiceId ? { ...inv, saveState: "saving" } : inv
    ));
    const ok = await saveSettlementToDB(latest);
    setInvoices((prev) => prev.map((inv) =>
      inv.id === invoiceId ? { ...inv, saveState: ok ? "saved" : "error" } : inv
    ));
    setTimeout(() => {
      setInvoices((prev) => prev.map((inv) =>
        inv.id === invoiceId && inv.saveState === "saved" ? { ...inv, saveState: "idle" } : inv
      ));
    }, 3000);
  }

  const filtered = invoices.filter((inv) => {
    const searchStr = `${safeToString(inv.lrNumber)} ${safeToString(inv.invoiceNumber)} ${safeToString(inv.company)} ${safeToString(inv.origin)} ${safeToString(inv.destination)} ${safeToString(inv.sender)} ${safeToString(inv.receiver)} ${safeToString(inv.driver)} ${safeToString(inv.vehicle)} ${safeToString(inv.broker)}`.toLowerCase();
    if (search && !searchStr.includes(search.toLowerCase())) return false;

    if (filterStatus && inv.status !== filterStatus) return false;

    if (filterCompany && safeToString(inv.company).toLowerCase() !== filterCompany.toLowerCase()) {
      return false;
    }

    if (filterParty) {
      const targetP = filterParty.toLowerCase();
      const matchParty =
        safeToString(inv.sender).toLowerCase() === targetP ||
        safeToString(inv.receiver).toLowerCase() === targetP ||
        safeToString(inv.transporter).toLowerCase() === targetP ||
        safeToString(inv.driver).toLowerCase() === targetP ||
        safeToString(inv.broker).toLowerCase() === targetP ||
        inv.userDistributions.some((d) => safeToString(d.userName).toLowerCase() === targetP);
      if (!matchParty) return false;
    }

    if (filterVehicle && safeToString(inv.vehicle).toLowerCase() !== filterVehicle.toLowerCase()) {
      return false;
    }

    if (filterUserType) {
      const hasType = inv.userDistributions.some((d) => d.userType === filterUserType) ||
        (filterUserType === "driver" && inv.driver && inv.driver !== "—") ||
        (filterUserType === "vehicle" && inv.vehicle && inv.vehicle !== "—") ||
        (filterUserType === "broker" && inv.broker && inv.broker !== "—") ||
        (filterUserType === "receiver" && inv.receiver && inv.receiver !== "—") ||
        (filterUserType === "sender" && inv.sender && inv.sender !== "—");
      if (!hasType) return false;
    }

    if (filterDateFrom) {
      const d = inv.date ? inv.date.slice(0, 10) : "";
      if (!d || d < filterDateFrom) return false;
    }
    if (filterDateTo) {
      const d = inv.date ? inv.date.slice(0, 10) : "";
      if (!d || d > filterDateTo) return false;
    }

    if (filterDeductionType) {
      if (filterDeductionType === "any") {
        const hasDeductions = inv.userDistributions.some((d) =>
          ((d.commission || 0) + (d.tds || 0) + (d.collection || 0) + (d.otherDeduction || 0)) > 0
        );
        if (!hasDeductions) return false;
      } else if (filterDeductionType === "commission") {
        if (!inv.userDistributions.some((d) => (d.commission || 0) > 0)) return false;
      } else if (filterDeductionType === "tds") {
        if (!inv.userDistributions.some((d) => (d.tds || 0) > 0)) return false;
      } else if (filterDeductionType === "collection") {
        if (!inv.userDistributions.some((d) => (d.collection || 0) > 0)) return false;
      } else if (filterDeductionType === "other") {
        if (!inv.userDistributions.some((d) => (d.otherDeduction || 0) > 0)) return false;
      }
    }

    return true;
  });

  function openPay(invoiceId:string, userIdx:number, amt:number, mode:PaymentMode, date:string, full=false, editing=false, editingIdx?:number) {
    setPayModal({ invoiceId, userIdx, amt:amt.toString(), mode, date, full, editing, editingIdx });
  }

  function handlePayment(invoiceId:string, userIdx:number, amt:number, mode:PaymentMode, date:string, editingIdx?:number) {
    let updatedInvoice: Invoice | undefined;
    setInvoices((prev) => prev.map((inv) => {
      if (inv.id !== invoiceId) return inv;
      const dists = inv.userDistributions.map((u, i) => {
        if (i !== userIdx) return u;
        const netPay = getNetPayable(u);
        const otherPaid = u.paymentRecords.reduce((sum, r, idx) =>
          sum + (editingIdx === idx ? 0 : r.amount), 0
        );
        const maxAllowed = Math.max(0, netPay - otherPaid);
        const safeAmt = Math.min(amt, maxAllowed);

        let recs = [...u.paymentRecords];
        if (typeof editingIdx === "number") recs[editingIdx] = { amount:safeAmt, paymentMode:mode, date };
        else recs.push({ amount:safeAmt, paymentMode:mode, date });
        recs = recs.filter((r) => r.amount > 0);
        return { ...u, paymentRecords:recs, paid:recs.reduce((a,r)=>a+r.amount,0) >= netPay };
      });
      const status = dists.every((d)=>d.paid)?"paid":dists.some((d)=>sumPaid(d)>0)?"partial":"generated";
      updatedInvoice = { ...inv, userDistributions:dists, status, saveState: "idle" };
      return updatedInvoice;
    }));
    setPayModal(null);
    setTimeout(() => {
      if (updatedInvoice) triggerSaveLatest(invoiceId, updatedInvoice);
    }, 100);
  }

  function removePayRec(invoiceId:string, userIdx:number, payIdx:number) {
    let updatedInvoice: Invoice | undefined;
    setInvoices((prev) => prev.map((inv) => {
      if (inv.id !== invoiceId) return inv;
      const dists = inv.userDistributions.map((u,i) => {
        if (i !== userIdx) return u;
        const recs = u.paymentRecords.filter((_,j)=>j!==payIdx);
        return { ...u, paymentRecords:recs, paid:recs.reduce((a,r)=>a+r.amount,0) >= getNetPayable(u) };
      });
      const status = dists.every((d)=>d.paid)?"paid":dists.some((d)=>sumPaid(d)>0)?"partial":"generated";
      updatedInvoice = { ...inv, userDistributions:dists, status };
      return updatedInvoice;
    }));
    setTimeout(() => {
      if (updatedInvoice) triggerSaveLatest(invoiceId, updatedInvoice);
    }, 100);
  }

  function openEditDist(invoice: Invoice, idx: number | null, u?: InvoiceUserPayment) {
    const remaining = Math.max(0, invoice.payment - sumAllocated(invoice.userDistributions));
    if (u) {
      const participants = getInvoiceParticipants(invoice);
      const isAuto = participants.some((p) => p.userType === u.userType && p.userName === u.userName);
      setEditDist({ invoiceId: invoice.id, editingIdx: idx, source: isAuto?"auto":"manual",
        selectedParticipant: isAuto?`${u.userType}::${u.userName}`:"",
        userType: u.userType, userName: u.userName, amount: u.amount?.toString()||"",
        commission: u.commission?.toString()||"", tds: u.tds?.toString()||"",
        collection: u.collection?.toString()||"",
        otherDeduction: u.otherDeduction?.toString()||"", otherDeductionName: u.otherDeductionName||"" });
    } else {
      const participants = getInvoiceParticipants(invoice);
      const firstP = participants[0];
      setEditDist({ invoiceId: invoice.id, editingIdx: null, source: "auto",
        selectedParticipant: firstP?`${firstP.userType}::${firstP.userName}`:"",
        userType: firstP?.userType||"", userName: firstP?.userName||"",
        amount: remaining > 0 ? remaining.toString() : "",
        commission: "", tds: "", collection: "", otherDeduction: "", otherDeductionName: "" });
    }
  }

  function handleParticipantSelect(key: string, participants: InvoiceParticipant[]) {
    if (key === "__manual__") {
      setEditDist((p) => p ? { ...p, source:"manual", selectedParticipant:"", userType:"", userName:"" } : p);
    } else {
      const found = participants.find((p) => `${p.userType}::${p.userName}` === key);
      if (found) {
        setEditDist((p) => p ? { ...p, source:"auto", selectedParticipant:key, userType:found.userType, userName:found.userName } : p);
      }
    }
  }

  function saveEditDist() {
    if (!editDist) return;
    const { invoiceId, editingIdx, userType, userName, amount, commission, tds, collection, otherDeduction, otherDeductionName } = editDist;
    const grossAmt = Number(amount || 0);
    if (!userType || grossAmt < 1) return;

    const commVal = Number(commission || 0);
    const tdsVal = Number(tds || 0);
    const collVal = Number(collection || 0);
    const othVal = Number(otherDeduction || 0);
    const othName = otherDeductionName.trim();
    if (commVal + tdsVal + collVal + othVal > grossAmt) return;
    const netPayable = Math.max(0, grossAmt - commVal - tdsVal - collVal - othVal);

    let updatedInvoice: Invoice | undefined;
    setInvoices((prev) => prev.map((inv) => {
      if (inv.id !== invoiceId) return inv;
      const others = inv.userDistributions.filter((_,i)=>i!==editingIdx);
      if (sumAllocated(others) + grossAmt > inv.payment) return inv;
      let list = [...inv.userDistributions];
      const existingRecs = editingIdx != null ? inv.userDistributions[editingIdx].paymentRecords : [];
      const existingPaid = existingRecs.reduce((a,r)=>a+r.amount,0);
      const entry: InvoiceUserPayment = {
        userType, userName, amount: grossAmt,
        commission: commVal, tds: tdsVal, collection: collVal, otherDeduction: othVal,
        otherDeductionName: othName, payableAmount: netPayable,
        paid: existingPaid >= netPayable,
        paymentRecords: existingRecs,
      };
      if (editingIdx != null) list[editingIdx] = entry;
      else list.push(entry);
      const status = list.every((d)=>d.paid)?"paid":list.some((d)=>sumPaid(d)>0)?"partial":"generated";
      updatedInvoice = { ...inv, userDistributions:list, status };
      return updatedInvoice;
    }));
    setEditDist(null);
    setTimeout(() => {
      if (updatedInvoice) triggerSaveLatest(invoiceId, updatedInvoice);
    }, 100);
  }

  function removeDist(invoiceId:string, idx:number) {
    let updatedInvoice: Invoice | undefined;
    setInvoices((prev) => prev.map((inv) => {
      if (inv.id !== invoiceId) return inv;
      const list = inv.userDistributions.filter((_,i)=>i!==idx);
      setSelectedUserTab((t)=>({ ...t, [invoiceId]:Math.max(0,Math.min(t[invoiceId]??0,list.length-1)) }));
      const status = list.every((d)=>d.paid)?"paid":list.some((d)=>sumPaid(d)>0)?"partial":"generated";
      updatedInvoice = { ...inv, userDistributions:list, status };
      return updatedInvoice;
    }));
    setTimeout(() => {
      if (updatedInvoice) triggerSaveLatest(invoiceId, updatedInvoice);
    }, 100);
  }

  // Default select first invoice if none selected
  useEffect(() => {
    if (filtered.length > 0 && (!expandedId || !filtered.some((i) => i.id === expandedId))) {
      setExpandedId(filtered[0].id);
    }
  }, [filtered, expandedId]);

  const selectedInvoice = invoices.find((i) => i.id === expandedId);

  return (
    <div className="h-[calc(100vh-64px)] bg-slate-100 flex flex-col p-4 sm:p-6 overflow-hidden w-full max-w-none">
      {/* Top Header Bar */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between pb-4 shrink-0">
        <div>
          <h1 className="text-2xl sm:text-3xl lg:text-4xl font-black text-slate-900 tracking-tight">
            Settlement Center
          </h1>
          <div className="text-xs sm:text-sm text-slate-500 font-medium mt-0.5">Side-by-Side Invoice Settlement & Payout Management</div>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <button
            onClick={() => navigate("/admin/Settlementrecord")}
            className="rounded-xl px-4 py-2 bg-white text-blue-700 text-xs sm:text-sm font-bold border border-blue-200 shadow-xs hover:bg-blue-600 hover:text-white transition flex items-center gap-2"
          >
            <FaHistory /> View Records
          </button>
          <div className="relative w-56 sm:w-64">
            <FaSearch className="absolute top-1/2 left-3.5 -translate-y-1/2 text-slate-400 text-sm" />
            <input
              type="text"
              placeholder="Search LR / Company / Driver…"
              className="pl-9 pr-4 py-2 rounded-xl border border-slate-300 font-semibold text-xs sm:text-sm text-slate-800 bg-white focus:border-blue-500 focus:outline-none w-full shadow-2xs"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <button
            onClick={() => setShowAdvanceFilters((p) => !p)}
            className={`rounded-xl px-4 py-2 font-bold text-xs sm:text-sm transition flex items-center gap-2 border shadow-xs ${
              showAdvanceFilters || activeFilterCount > 0
                ? "bg-blue-600 text-white border-blue-600"
                : "bg-white text-slate-700 border-slate-300 hover:bg-slate-50"
            }`}
          >
            <FaFilter /> Advance Filters
            {activeFilterCount > 0 && (
              <span className="bg-white text-blue-800 rounded-full text-[11px] px-2 py-0.5 font-black">
                {activeFilterCount}
              </span>
            )}
          </button>
          <button
            className="rounded-xl px-4 py-2 bg-blue-600 text-white text-xs sm:text-sm font-bold shadow-xs hover:bg-black transition disabled:opacity-60 flex items-center gap-2"
            onClick={load}
            disabled={loading}
          >
            {loading ? <FaSpinner className="animate-spin" /> : <FaSyncAlt />} Refresh
          </button>
        </div>
      </div>

      {/* Advanced Filters Expandable Panel */}
      {showAdvanceFilters && (
        <div className="bg-white border border-slate-200 rounded-2xl p-4 mb-4 shadow-xs flex flex-wrap gap-4 items-end text-xs sm:text-sm shrink-0">
          <div>
            <label className="block font-bold text-slate-700 mb-1">Company Name</label>
            <select
              value={filterCompany}
              onChange={(e) => setFilterCompany(e.target.value)}
              className="rounded-xl border border-slate-300 px-3 py-1.5 font-semibold text-slate-800 bg-slate-50 focus:border-blue-500 focus:outline-none max-w-[200px]"
            >
              <option value="">All Companies</option>
              {uniqueCompanies.map((c) => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block font-bold text-slate-700 mb-1">Party Name</label>
            <select
              value={filterParty}
              onChange={(e) => setFilterParty(e.target.value)}
              className="rounded-xl border border-slate-300 px-3 py-1.5 font-semibold text-slate-800 bg-slate-50 focus:border-blue-500 focus:outline-none max-w-[200px]"
            >
              <option value="">All Parties</option>
              {uniqueParties.map((p) => (
                <option key={p} value={p}>{p}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block font-bold text-slate-700 mb-1">Vehicle No.</label>
            <select
              value={filterVehicle}
              onChange={(e) => setFilterVehicle(e.target.value)}
              className="rounded-xl border border-slate-300 px-3 py-1.5 font-semibold text-slate-800 bg-slate-50 focus:border-blue-500 focus:outline-none max-w-[180px]"
            >
              <option value="">All Vehicles</option>
              {uniqueVehicles.map((v) => (
                <option key={v} value={v}>{v}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block font-bold text-slate-700 mb-1">Status</label>
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="rounded-xl border border-slate-300 px-3 py-1.5 font-semibold text-slate-800 bg-slate-50 focus:border-blue-500 focus:outline-none"
            >
              <option value="">All Statuses</option>
              <option value="generated">Unsettled (Pending)</option>
              <option value="partial">Partial</option>
              <option value="paid">Fully Paid</option>
            </select>
          </div>

          <div>
            <label className="block font-bold text-slate-700 mb-1">Entity / Type</label>
            <select
              value={filterUserType}
              onChange={(e) => setFilterUserType(e.target.value)}
              className="rounded-xl border border-slate-300 px-3 py-1.5 font-semibold text-slate-800 bg-slate-50 focus:border-blue-500 focus:outline-none capitalize"
            >
              <option value="">All Entity Types</option>
              <option value="driver">Driver</option>
              <option value="vehicle">Vehicle</option>
              <option value="broker">Broker</option>
              <option value="receiver">Receiver</option>
              <option value="sender">Sender</option>
            </select>
          </div>

          <div>
            <label className="block font-bold text-slate-700 mb-1">Advance / Deductions</label>
            <select
              value={filterDeductionType}
              onChange={(e) => setFilterDeductionType(e.target.value)}
              className="rounded-xl border border-slate-300 px-3 py-1.5 font-semibold text-slate-800 bg-slate-50 focus:border-blue-500 focus:outline-none"
            >
              <option value="">All Invoices</option>
              <option value="any">Has Any Deductions</option>
              <option value="commission">Commission &gt; ₹0</option>
              <option value="tds">TDS &gt; ₹0</option>
              <option value="collection">Collection &gt; ₹0</option>
              <option value="other">Other Deduction &gt; ₹0</option>
            </select>
          </div>

          <div>
            <label className="block font-bold text-slate-700 mb-1">From Date</label>
            <input
              type="date"
              value={filterDateFrom}
              onChange={(e) => setFilterDateFrom(e.target.value)}
              className="rounded-xl border border-slate-300 px-3 py-1.5 font-semibold text-slate-800 bg-slate-50 focus:border-blue-500 focus:outline-none"
            />
          </div>

          <div>
            <label className="block font-bold text-slate-700 mb-1">To Date</label>
            <input
              type="date"
              value={filterDateTo}
              onChange={(e) => setFilterDateTo(e.target.value)}
              className="rounded-xl border border-slate-300 px-3 py-1.5 font-semibold text-slate-800 bg-slate-50 focus:border-blue-500 focus:outline-none"
            />
          </div>

          {(activeFilterCount > 0 || search) && (
            <button
              onClick={resetFilters}
              className="px-3.5 py-1.5 bg-slate-100 hover:bg-red-50 text-red-600 border border-slate-300 rounded-xl font-bold transition flex items-center gap-1.5"
            >
              <FaUndo /> Reset Filters
            </button>
          )}
        </div>
      )}

      {loading && (
        <div className="flex-1 flex flex-col items-center justify-center bg-white rounded-2xl border border-slate-200 shadow-xs">
          <FaSpinner className="text-blue-600 text-4xl animate-spin mb-3" />
          <span className="text-slate-700 font-bold text-base">Loading settlement data…</span>
        </div>
      )}
      {!loading && error && (
        <div className="flex-1 flex flex-col items-center justify-center bg-white rounded-2xl border border-slate-200 shadow-xs p-8">
          <FaExclamationTriangle className="text-red-500 text-4xl mb-3" />
          <div className="text-red-700 font-bold text-base text-center mb-4">{error}</div>
          <button onClick={load} className="px-5 py-2 bg-blue-600 text-white rounded-xl font-bold text-sm hover:bg-black transition">Retry</button>
        </div>
      )}

      {!loading && !error && (
        <div className="flex-1 flex gap-6 lg:gap-8 min-h-0 overflow-hidden">
          {/* LEFT PANEL: Master Invoice List */}
          <div className="w-80 lg:w-96 shrink-0 bg-white rounded-2xl border border-slate-200 shadow-xs flex flex-col overflow-hidden">
            <div className="p-3 bg-slate-50 border-b border-slate-200 text-xs font-bold text-slate-700 flex justify-between items-center shrink-0">
              <span>Invoices ({filtered.length})</span>
              <span className="text-[11px] text-slate-400 font-medium">Click to select</span>
            </div>

            <div className="flex-1 overflow-y-auto divide-y divide-slate-100">
              {filtered.length === 0 ? (
                <div className="p-6 text-center text-xs text-slate-400 font-medium">No matching invoices found.</div>
              ) : (
                filtered.map((inv) => {
                  const isSelected     = inv.id === expandedId;
                  const totalAllocated = sumAllocated(inv.userDistributions);
                  const remaining      = inv.payment - totalAllocated;

                  return (
                    <div
                      key={inv.id}
                      onClick={() => setExpandedId(inv.id)}
                      className={`p-3 cursor-pointer transition flex flex-col gap-1.5 text-xs border-l-4 ${
                        isSelected
                          ? "bg-blue-50/90 border-l-blue-600 font-semibold"
                          : "bg-white border-l-transparent hover:bg-slate-50/80"
                      }`}
                    >
                      <div className="flex items-center justify-between gap-1">
                        <span className="font-extrabold text-slate-900 truncate text-sm capitalize">{safeToString(inv.company)}</span>
                        <span className={`px-1.5 py-0.5 rounded text-[10px] font-extrabold uppercase shrink-0 ${
                          inv.status === "paid" ? "bg-green-100 text-green-800" : inv.status === "partial" ? "bg-amber-100 text-amber-800" : "bg-slate-100 text-slate-700"
                        }`}>
                          {inv.status}
                        </span>
                      </div>

                      <div className="flex items-center justify-between text-slate-600 text-[11px]">
                        <span>LR: <b className="text-blue-700">{safeToString(inv.lrNumber)}</b></span>
                        {inv.date && <span>📅 {fmtDate(inv.date)}</span>}
                      </div>

                      <div className="flex items-center justify-between pt-1 border-t border-slate-100/60">
                        <span className="font-bold text-slate-800">Total: <b className="text-green-700">₹{inv.payment.toLocaleString("en-IN")}</b></span>
                        <span className="text-[11px]">
                          Unallocated: <b className={remaining === 0 ? "text-green-700" : "text-amber-700"}>₹{remaining.toLocaleString("en-IN")}</b>
                        </span>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>

          {/* RIGHT PANEL: Detail Settlement Workspace */}
          <div className="flex-1 bg-white rounded-2xl border border-slate-200 shadow-xs flex flex-col overflow-hidden min-w-0">
            {!selectedInvoice ? (
              <div className="flex-1 flex flex-col items-center justify-center p-6 text-slate-400 text-xs font-medium">
                <FaTruckMoving className="text-4xl text-slate-300 mb-2" />
                Select an invoice from the left to view settlement details.
              </div>
            ) : (
              (() => {
                const invoice        = selectedInvoice;
                const totalAllocated = sumAllocated(invoice.userDistributions);
                const remaining      = invoice.payment - totalAllocated;
                const participants   = getInvoiceParticipants(invoice);
                const totalWeight    = invoice.items.reduce((s, it) => s + Number(it.qty || 0), 0);
                const uom            = invoice.items.find((it) => it.uom && it.uom !== "—")?.uom || "";

                return (
                  <>
                    {/* Header Bar */}
                    <div className="p-3.5 bg-slate-50 border-b border-slate-200 flex flex-wrap items-center justify-between gap-3 shrink-0">
                      <div className="min-w-0">
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="font-extrabold text-base text-slate-900 truncate capitalize">{safeToString(invoice.company)}</span>
                          <span className="text-xs font-bold text-blue-700 bg-blue-50 border border-blue-200 px-2 py-0.5 rounded">LR: #{safeToString(invoice.lrNumber)}</span>
                          <span className="text-xs font-bold text-slate-600 bg-slate-100 px-2 py-0.5 rounded">Inv: #{safeToString(invoice.invoiceNumber)}</span>
                          {totalWeight > 0 && (
                            <span className="text-indigo-900 bg-indigo-50 border border-indigo-200 font-bold px-2 py-0.5 rounded text-xs">
                              ⚖️ {totalWeight.toLocaleString("en-IN")} {uom}
                            </span>
                          )}
                        </div>
                        <div className="text-xs text-slate-500 font-medium mt-1">
                          Invoice Total: <b className="text-green-700 font-extrabold">₹{invoice.payment.toLocaleString("en-IN")}</b> &nbsp;|&nbsp;
                          Allocated: <b className="text-blue-700">₹{totalAllocated.toLocaleString("en-IN")}</b> &nbsp;|&nbsp;
                          Unallocated Balance: <b className={remaining === 0 ? "text-green-700" : "text-amber-700"}>₹{remaining.toLocaleString("en-IN")}</b>
                        </div>
                      </div>

                      <div className="flex items-center gap-2 shrink-0">
                        {loadingSettlement[invoice.id] && (
                          <span className="text-xs text-blue-600 flex items-center gap-1 font-semibold">
                            <FaSpinner className="animate-spin" /> Saving…
                          </span>
                        )}
                        <SaveButton
                          state={invoice.saveState ?? "idle"}
                          onClick={() => triggerSaveLatest(invoice.id, invoice)}
                        />
                      </div>
                    </div>

                    {/* Scrollable Main Workspace */}
                    <div className="flex-1 overflow-y-auto p-4 space-y-4">
                      {/* SECTION 1: Participant Allocation Bar */}
                      <div className="bg-slate-50 border border-slate-200 rounded-xl p-3 shadow-2xs">
                        <div className="text-xs font-bold text-slate-700 uppercase tracking-wide mb-2 flex justify-between items-center">
                          <span>Quick Participant Allocation</span>
                          <span className="text-slate-500 font-semibold text-[11px]">
                            Remaining Balance: <b className={remaining === 0 ? "text-green-700" : "text-amber-700"}>₹{remaining.toLocaleString("en-IN")}</b>
                          </span>
                        </div>
                        <div className="flex flex-wrap gap-2">
                          {participants.map((p) => {
                            const alreadyAdded = invoice.userDistributions.some(
                              (d) => d.userType === p.userType && d.userName === p.userName
                            );
                            return (
                              <div
                                key={`${p.userType}::${p.userName}`}
                                className={`flex items-center gap-2 rounded-lg px-2.5 py-1 text-xs border ${
                                  alreadyAdded ? "bg-green-50 border-green-200 text-green-800" : "bg-white border-slate-200 text-slate-800"
                                }`}
                              >
                                <span className="font-bold capitalize text-blue-700">{userTypeLabel[p.userType] || p.userType}:</span>
                                <span className="font-semibold">{safeToString(p.userName)}</span>
                                {alreadyAdded ? (
                                  <span className="text-[11px] text-green-700 font-bold flex items-center gap-0.5 ml-1">
                                    <FaCheckCircle /> Added
                                  </span>
                                ) : (
                                  <button
                                    disabled={remaining <= 0}
                                    className="ml-1 px-2 py-0.5 rounded bg-blue-600 text-white font-bold hover:bg-black disabled:opacity-40 transition"
                                    onClick={() => {
                                      setEditDist({
                                        invoiceId: invoice.id,
                                        editingIdx: null,
                                        source: "auto",
                                        selectedParticipant: `${p.userType}::${p.userName}`,
                                        userType: p.userType,
                                        userName: safeToString(p.userName),
                                        amount: remaining > 0 ? remaining.toString() : "",
                                        commission: "",
                                        tds: "",
                                        collection: "",
                                        otherDeduction: "",
                                        otherDeductionName: "",
                                      });
                                    }}
                                  >
                                    + Allocate
                                  </button>
                                )}
                              </div>
                            );
                          })}
                          <button
                            className={`px-3 py-1 rounded-lg text-xs font-bold border transition ${
                              remaining <= 0
                                ? "bg-slate-100 border-slate-300 text-slate-400 cursor-not-allowed"
                                : "bg-indigo-50 border-indigo-200 text-indigo-700 hover:bg-indigo-600 hover:text-white"
                            }`}
                            onClick={() => {
                              if (remaining > 0) openEditDist(invoice, null);
                              else alert("No remaining balance to allocate.");
                            }}
                          >
                            + Manual Entry
                          </button>
                        </div>
                      </div>

                      {/* SECTION 2: Active Distributions & Payouts */}
                      <div>
                        <div className="text-xs font-bold text-slate-800 uppercase tracking-wide mb-2">
                          Active Payout Distributions ({invoice.userDistributions.length})
                        </div>
                        {invoice.userDistributions.length === 0 ? (
                          <div className="text-center py-6 bg-slate-50 border border-slate-200 rounded-xl text-slate-400 font-medium text-xs">
                            No settlement users allocated yet. Click "+ Allocate" above to assign payout to driver, vehicle, or broker.
                          </div>
                        ) : (
                          <div className="space-y-3">
                            {invoice.userDistributions.map((u, idx) => {
                              const netPayable = getNetPayable(u);
                              const paidAmt    = sumPaid(u);
                              const remAmt     = Math.max(0, netPayable - paidAmt);
                              const isFullyPaid = paidAmt >= netPayable && netPayable > 0;

                              return (
                                <div
                                  key={`${u.userType}_${idx}`}
                                  className={`rounded-xl border-2 p-3 bg-white shadow-xs transition ${
                                    isFullyPaid ? "border-green-300 bg-green-50/20" : "border-slate-200"
                                  }`}
                                >
                                  <div className="flex flex-wrap items-center justify-between gap-2 border-b border-slate-100 pb-2">
                                    <div className="flex items-center gap-2">
                                      <span className="font-extrabold text-sm text-slate-900 capitalize">
                                        {userTypeLabel[u.userType] || u.userType}
                                        {u.userName && <span className="font-semibold text-slate-600 text-xs ml-1">: {safeToString(u.userName)}</span>}
                                      </span>
                                      <span className={`px-2 py-0.5 rounded text-[10px] font-bold ${
                                        isFullyPaid ? "bg-green-100 text-green-800" : paidAmt > 0 ? "bg-amber-100 text-amber-800" : "bg-slate-100 text-slate-700"
                                      }`}>
                                        {isFullyPaid ? "Fully Paid" : paidAmt > 0 ? "Partially Paid" : "Unpaid"}
                                      </span>
                                    </div>

                                    <div className="flex items-center gap-1.5">
                                      <button
                                        disabled={isFullyPaid}
                                        onClick={() => openPay(invoice.id, idx, remAmt, "bank", getTodayLocalDate(), true)}
                                        className="px-2.5 py-1 rounded bg-green-600 text-white font-bold text-xs hover:bg-black shadow disabled:opacity-40 transition flex items-center gap-1"
                                      >
                                        <FaMoneyCheckAlt /> Full Pay
                                      </button>
                                      <button
                                        disabled={isFullyPaid}
                                        onClick={() => openPay(invoice.id, idx, 0, "bank", getTodayLocalDate())}
                                        className="px-2.5 py-1 rounded bg-blue-600 text-white font-bold text-xs hover:bg-black shadow disabled:opacity-40 transition"
                                      >
                                        Partial Pay
                                      </button>
                                      <button
                                        onClick={() => openEditDist(invoice, idx, u)}
                                        className="p-1.5 rounded-xl text-slate-500 bg-slate-100 hover:bg-blue-600 hover:text-white border border-slate-300 transition"
                                        title="Edit Allocation"
                                      >
                                        <FaEdit />
                                      </button>
                                      <button
                                        onClick={() => removeDist(invoice.id, idx)}
                                        className="p-1.5 rounded-xl text-slate-500 bg-slate-100 hover:bg-red-600 hover:text-white border border-slate-300 transition"
                                        title="Delete Allocation"
                                      >
                                        <FaTrash />
                                      </button>
                                    </div>
                                  </div>

                                  {/* Financial Summary & Deductions Breakdown */}
                                  <div className="bg-slate-50 border border-slate-200 rounded-xl p-3.5 sm:p-4 mt-3 space-y-3">
                                    {/* Deductions Breakdown Details: Where and how much deducted */}
                                    <div className="flex flex-wrap items-center justify-between gap-2 text-xs sm:text-sm border-b border-slate-200/80 pb-3">
                                      <div className="font-bold text-slate-800">
                                        Gross Allocated: <span className="font-extrabold text-blue-900 text-sm sm:text-base ml-1">₹{u.amount?.toLocaleString("en-IN")}</span>
                                      </div>

                                      <div className="flex flex-wrap items-center gap-1.5">
                                        <span className="text-[11px] font-bold text-red-700 uppercase tracking-wider mr-1">Deduction Details:</span>
                                        {(u.commission || 0) > 0 && (
                                          <span className="bg-red-100 text-red-800 border border-red-200 text-xs px-2.5 py-0.5 rounded-md font-bold">
                                            Commission: -₹{u.commission?.toLocaleString("en-IN")}
                                          </span>
                                        )}
                                        {(u.tds || 0) > 0 && (
                                          <span className="bg-red-100 text-red-800 border border-red-200 text-xs px-2.5 py-0.5 rounded-md font-bold">
                                            TDS: -₹{u.tds?.toLocaleString("en-IN")}
                                          </span>
                                        )}
                                        {(u.collection || 0) > 0 && (
                                          <span className="bg-red-100 text-red-800 border border-red-200 text-xs px-2.5 py-0.5 rounded-md font-bold">
                                            Collection: -₹{u.collection?.toLocaleString("en-IN")}
                                          </span>
                                        )}
                                        {(u.otherDeduction || 0) > 0 && (
                                          <span className="bg-red-100 text-red-800 border border-red-200 text-xs px-2.5 py-0.5 rounded-md font-bold">
                                            {u.otherDeductionName?.trim() || "Other"}: -₹{u.otherDeduction?.toLocaleString("en-IN")}
                                          </span>
                                        )}
                                        {((u.commission || 0) + (u.tds || 0) + (u.collection || 0) + (u.otherDeduction || 0)) === 0 && (
                                          <span className="text-xs text-slate-400 font-medium italic">None (₹0)</span>
                                        )}

                                        <span className="bg-red-600 text-white font-extrabold text-xs px-2.5 py-0.5 rounded-md ml-1 shadow-2xs">
                                          Total Deducted: -₹{((u.commission || 0) + (u.tds || 0) + (u.collection || 0) + (u.otherDeduction || 0)).toLocaleString("en-IN")}
                                        </span>
                                      </div>
                                    </div>

                                    {/* Metrics Cards */}
                                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs sm:text-sm">
                                      <div className="bg-indigo-50/80 p-2.5 rounded-xl border border-indigo-100">
                                        <span className="text-[11px] text-indigo-700 font-bold uppercase block">Net Payable</span>
                                        <span className="font-extrabold text-indigo-950 text-sm sm:text-base">₹{netPayable.toLocaleString("en-IN")}</span>
                                      </div>
                                      <div className="bg-green-50/80 p-2.5 rounded-xl border border-green-100">
                                        <span className="text-[11px] text-green-700 font-bold uppercase block">Total Paid</span>
                                        <span className="font-bold text-green-800 text-sm sm:text-base">₹{paidAmt.toLocaleString("en-IN")}</span>
                                      </div>
                                      <div className="bg-amber-50/80 p-2.5 rounded-xl border border-amber-100">
                                        <span className="text-[11px] text-amber-700 font-bold uppercase block">Remaining Due</span>
                                        <span className="font-bold text-amber-800 text-sm sm:text-base">₹{remAmt.toLocaleString("en-IN")}</span>
                                      </div>
                                      <div className="bg-red-50/80 p-2.5 rounded-xl border border-red-100">
                                        <span className="text-[11px] text-red-600 font-bold uppercase block">Total Deducted</span>
                                        <span className="font-bold text-red-700 text-sm sm:text-base">
                                          -₹{((u.commission || 0) + (u.tds || 0) + (u.collection || 0) + (u.otherDeduction || 0)).toLocaleString("en-IN")}
                                        </span>
                                      </div>
                                    </div>
                                  </div>

                                  {/* Payment History Table */}
                                  {u.paymentRecords.length > 0 && (
                                    <div className="mt-3 pt-2 border-t border-slate-100">
                                      <div className="text-[11px] font-bold text-slate-600 mb-1">Payment History ({u.paymentRecords.length})</div>
                                      <div className="overflow-x-auto">
                                        <table className="w-full text-xs border bg-slate-50/50 rounded-lg">
                                          <thead>
                                            <tr className="bg-slate-100 text-slate-700 font-bold border-b">
                                              <th className="p-1 text-left">Date</th>
                                              <th className="p-1 text-left">Amount</th>
                                              <th className="p-1 text-left">Mode</th>
                                              <th className="p-1 text-right">Actions</th>
                                            </tr>
                                          </thead>
                                          <tbody>
                                            {u.paymentRecords.map((rec, rIdx) => (
                                              <tr key={rIdx} className="border-b border-slate-200/60 bg-white">
                                                <td className="p-1 font-medium text-slate-800">{fmtDate(rec.date)}</td>
                                                <td className="p-1 font-bold text-green-700">₹{rec.amount?.toLocaleString("en-IN")}</td>
                                                <td className="p-1">{paymentModeLabel(rec.paymentMode)}</td>
                                                <td className="p-1 text-right">
                                                  <button
                                                    className="mr-1 px-1.5 py-0.5 text-[10px] bg-blue-100 rounded text-blue-700 font-bold hover:bg-blue-600 hover:text-white"
                                                    onClick={() => openPay(invoice.id, idx, rec.amount, rec.paymentMode, rec.date, false, true, rIdx)}
                                                  >
                                                    Edit
                                                  </button>
                                                  <button
                                                    className="px-1.5 py-0.5 text-[10px] bg-slate-200 rounded text-slate-700 font-bold hover:bg-red-600 hover:text-white"
                                                    onClick={() => removePayRec(invoice.id, idx, rIdx)}
                                                  >
                                                    Delete
                                                  </button>
                                                </td>
                                              </tr>
                                            ))}
                                          </tbody>
                                        </table>
                                      </div>
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>

                      {/* SECTION 3: Invoice Parties & Line Items */}
                      <div className="space-y-3 pt-2 border-t border-slate-200">
                        <div className="text-xs font-bold text-slate-800 uppercase tracking-wide">
                          Invoice Parties & Breakdown
                        </div>
                        <div className="bg-slate-50 border border-slate-200 rounded-xl p-3 text-xs grid grid-cols-2 gap-2">
                          <div><b>Sender:</b> {safeToString(invoice.sender)}</div>
                          <div><b>Receiver:</b> {safeToString(invoice.receiver)}</div>
                          <div><b>Transporter:</b> {safeToString(invoice.transporter)}</div>
                          <div><b>Driver:</b> {safeToString(invoice.driver)}</div>
                          {invoice.broker && safeToString(invoice.broker) !== "—" && (
                            <div><b>Broker:</b> {safeToString(invoice.broker)}</div>
                          )}
                        </div>

                        {invoice.items.length > 0 && (
                          <div className="border border-slate-200 rounded-xl overflow-x-auto bg-white">
                            <table className="w-full text-xs">
                              <thead className="bg-slate-100 border-b text-slate-800 font-bold">
                                <tr>
                                  <th className="p-1.5 text-center">#</th>
                                  <th className="p-1.5 text-left">Product / Item</th>
                                  <th className="p-1.5 text-center">HSN</th>
                                  <th className="p-1.5 text-center">UOM</th>
                                  <th className="p-1.5 text-center">Weight / Qty</th>
                                  <th className="p-1.5 text-center">Rate (₹)</th>
                                  <th className="p-1.5 text-right">Amount (₹)</th>
                                </tr>
                              </thead>
                              <tbody>
                                {invoice.items.map((it, i) => (
                                  <tr key={it.id} className="border-b border-slate-100 hover:bg-blue-50/30">
                                    <td className="p-1.5 text-center text-slate-500">{i + 1}</td>
                                    <td className="p-1.5 font-bold text-blue-900">{safeToString(it.product)}</td>
                                    <td className="p-1.5 text-center text-slate-600">{safeToString(it.hsn)}</td>
                                    <td className="p-1.5 text-center text-slate-600">{safeToString(it.uom)}</td>
                                    <td className="p-1.5 text-center font-bold text-indigo-900">{it.qty ? `${it.qty} ${it.uom !== "—" ? it.uom : ""}` : "—"}</td>
                                    <td className="p-1.5 text-center">{it.rate ? `₹${it.rate.toLocaleString("en-IN")}` : "—"}</td>
                                    <td className="p-1.5 font-bold text-green-700 text-right">{it.amount ? `₹${it.amount.toLocaleString("en-IN")}` : "—"}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        )}
                      </div>
                    </div>
                  </>
                );
              })()
            )}
          </div>
        </div>
      )}

        {payModal && (() => {
          const invoice = invoices.find((i) => i.id === payModal.invoiceId);
          if (!invoice) return null;
          const u = invoice.userDistributions[payModal.userIdx];
          if (!u) return null;
          const netPay = getNetPayable(u);
          const paidSoFar = u.paymentRecords.reduce((sum, r, idx) =>
            sum + (payModal.editing && payModal.editingIdx === idx ? 0 : r.amount), 0
          );
          const remToPay = Math.max(0, netPay - paidSoFar);
          const isFull = !!payModal.full;
          const isExceeding = !isFull && Number(payModal.amt) > remToPay;

          return (
            <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-2">
              <div className="bg-white p-5 rounded-xl border-2 border-blue-600 shadow-2xl w-84 max-w-[95vw] space-y-3">
                <div className="text-black font-bold text-base border-b border-gray-100 pb-2">
                  {payModal.editing ? "Edit Payment" : isFull ? "Full Payment" : "Partial Payment"} for <span className="text-blue-700 capitalize">{userTypeLabel[u.userType] ?? u.userType}</span>
                  {u.userName && <span className="text-gray-600 font-normal text-xs"> ({safeToString(u.userName)})</span>}
                </div>

                <div className="bg-blue-50/80 border border-blue-200 rounded-lg p-2.5 text-xs space-y-1 text-blue-900">
                  <div className="flex justify-between">
                    <span className="font-medium text-gray-600">Net Payable:</span>
                    <span className="font-bold text-blue-950">₹{netPay.toLocaleString("en-IN")}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="font-medium text-gray-600">Already Paid:</span>
                    <span className="font-bold text-green-700">₹{paidSoFar.toLocaleString("en-IN")}</span>
                  </div>
                  <div className="flex justify-between border-t border-blue-200/80 pt-1 font-black">
                    <span className="text-blue-900 uppercase text-[10px]">Amount Due to Pay:</span>
                    <span className="text-amber-700 text-xs">₹{remToPay.toLocaleString("en-IN")}</span>
                  </div>
                </div>

                {isExceeding && (
                  <div className="text-[11px] text-red-600 font-bold bg-red-50 p-1.5 rounded border border-red-200">
                    ⚠️ Payment cannot exceed remaining due amount (Max: ₹{remToPay.toLocaleString("en-IN")})
                  </div>
                )}

                <div>
                  <label className="block text-xs font-bold mb-1 text-gray-800">
                    Payment Amount (₹) {remToPay > 0 && <span className="text-amber-700 font-bold ml-1">(Due: ₹{remToPay.toLocaleString("en-IN")})</span>}
                  </label>
                  <input
                    type="number" min={0} max={remToPay}
                    className="rounded px-2.5 py-1.5 border border-gray-300 text-black text-xs w-full font-semibold focus:border-blue-500 focus:outline-none"
                    value={isFull ? remToPay.toString() : payModal.amt}
                    readOnly={isFull}
                    onChange={(e) => setPayModal((p) => (p ? { ...p, amt: e.target.value.replace(/^0+/, "") } : p))}
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold mb-1 text-gray-800">Payment Mode</label>
                  <select
                    className="rounded px-2.5 py-1.5 border border-gray-300 text-black text-xs w-full focus:border-blue-500 focus:outline-none font-medium"
                    value={payModal.mode}
                    onChange={(e) => setPayModal((p) => (p ? { ...p, mode: e.target.value as PaymentMode } : p))}
                  >
                    <option value="bank">Bank</option>
                    <option value="online">Online</option>
                    <option value="cash">Cash</option>
                    <option value="partial">Partial</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-bold mb-1 text-gray-800">Payment Date</label>
                  <input
                    type="date"
                    className="rounded px-2.5 py-1.5 border border-gray-300 text-black text-xs w-full focus:border-blue-500 focus:outline-none font-semibold"
                    value={formatYMD(payModal.date)}
                    onChange={(e) => setPayModal((p) => (p ? { ...p, date: e.target.value } : p))}
                  />
                </div>
                <div className="flex gap-2 justify-end pt-2 border-t border-gray-100">
                  <button
                    className="bg-blue-600 rounded text-white text-xs px-3.5 py-1.5 font-bold hover:bg-black border-2 border-blue-600 disabled:opacity-40 disabled:cursor-not-allowed transition"
                    disabled={(!isFull && Number(payModal.amt) <= 0) || isExceeding}
                    onClick={() => {
                      const amt = isFull ? remToPay : Number(payModal.amt);
                      handlePayment(payModal.invoiceId, payModal.userIdx, amt, payModal.mode, payModal.date, payModal.editing ? payModal.editingIdx : undefined);
                    }}
                  >
                    {payModal.editing ? "Save Payment" : "Record Payment"}
                  </button>
                  <button onClick={() => setPayModal(null)} className="bg-black/80 text-white px-3 py-1.5 rounded text-xs hover:bg-blue-600 border border-black font-semibold transition">Cancel</button>
                </div>
              </div>
            </div>
          );
        })()}

        {editDist && (() => {
          const invoice = invoices.find((i) => i.id === editDist.invoiceId);
          if (!invoice) return null;
          const participants = getInvoiceParticipants(invoice);
          const maxAmt = editDist.editingIdx != null
            ? invoice.payment - sumAllocated(invoice.userDistributions.filter((_, i) => i !== editDist.editingIdx))
            : invoice.payment - sumAllocated(invoice.userDistributions);

          return (
            <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-2">
              <div className="bg-white p-5 rounded-xl border-2 border-blue-600 shadow-2xl w-[440px] max-w-[95vw] space-y-3 max-h-[90vh] overflow-y-auto">
                <div className="text-black font-bold text-lg">
                  {editDist.editingIdx != null ? "Edit Settlement User" : "Add Settlement User"}
                </div>
                <div className="flex gap-2 rounded-lg overflow-hidden border border-blue-200">
                  <button
                    className={`flex-1 py-1.5 text-xs font-bold transition ${editDist.source === "auto" ? "bg-blue-600 text-white" : "bg-white text-blue-700 hover:bg-blue-50"}`}
                    onClick={() => {
                      const first = participants[0];
                      setEditDist((p) => (p ? { ...p, source: "auto", selectedParticipant: first ? `${first.userType}::${first.userName}` : "", userType: first?.userType || "", userName: first?.userName || "" } : p));
                    }}
                  >
                    From Invoice
                  </button>
                  <button
                    className={`flex-1 py-1.5 text-xs font-bold transition ${editDist.source === "manual" ? "bg-blue-600 text-white" : "bg-white text-blue-700 hover:bg-blue-50"}`}
                    onClick={() => setEditDist((p) => (p ? { ...p, source: "manual", selectedParticipant: "", userType: "", userName: "" } : p))}
                  >
                    Manual Entry
                  </button>
                </div>
                {editDist.source === "auto" && (
                  <div>
                    <label className="block text-xs font-bold mb-1">Select Participant</label>
                    {participants.length === 0 ? (
                      <div className="text-xs text-gray-400 italic px-2 py-1">No participants found. Use Manual Entry.</div>
                    ) : (
                      <select
                        className="rounded px-2 py-1 border border-gray-300 text-black w-full text-xs"
                        value={editDist.selectedParticipant}
                        onChange={(e) => handleParticipantSelect(e.target.value, participants)}
                      >
                        <option value="">Select…</option>
                        {participants.map((p) => (
                          <option key={`${p.userType}::${safeToString(p.userName)}`} value={`${p.userType}::${safeToString(p.userName)}`}>
                            {userTypeLabel[p.userType] || p.userType}: {safeToString(p.userName)}
                          </option>
                        ))}
                      </select>
                    )}
                    {editDist.userType && (
                      <div className="mt-1 text-xs bg-blue-50 rounded px-2 py-1 text-blue-800 font-semibold border border-blue-100">
                        <span className="capitalize">{userTypeLabel[editDist.userType] || editDist.userType}</span>: {safeToString(editDist.userName)}
                      </div>
                    )}
                  </div>
                )}
                {editDist.source === "manual" && (
                  <>
                    <div>
                      <label className="block text-xs font-bold mb-1">User Type</label>
                      <select
                        className="rounded px-2 py-1 border border-gray-300 text-black w-full text-xs"
                        value={editDist.userType}
                        onChange={(e) => setEditDist((p) => (p ? { ...p, userType: e.target.value } : p))}
                      >
                        <option value="">Select…</option>
                        {presetUserTypes.map((t) => (
                          <option key={t} value={t}>{userTypeLabel[t] || t}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs font-bold mb-1">User Name</label>
                      <input
                        type="text"
                        className="rounded px-2 py-1 border border-gray-300 text-black w-full text-xs"
                        placeholder="Enter name…"
                        value={safeToString(editDist.userName)}
                        onChange={(e) => setEditDist((p) => (p ? { ...p, userName: e.target.value } : p))}
                      />
                    </div>
                  </>
                )}
                {(editDist.userType || editDist.source === "manual") && (
                  <>
                    <div>
                      <div className="flex justify-between items-center mb-1">
                        <label className="block text-xs font-bold text-gray-800">Gross Allocated Amount (₹)</label>
                        {maxAmt > 0 && (
                          <div className="flex gap-1 text-[10px]">
                            <button
                              type="button"
                              onClick={() => setEditDist((p) => (p ? { ...p, amount: maxAmt.toString() } : p))}
                              className="px-1.5 py-0.5 rounded bg-blue-100 text-blue-700 font-bold hover:bg-blue-600 hover:text-white transition"
                            >
                              Full Balance (₹{maxAmt.toLocaleString("en-IN")})
                            </button>
                            <button
                              type="button"
                              onClick={() => setEditDist((p) => (p ? { ...p, amount: Math.round(maxAmt / 2).toString() } : p))}
                              className="px-1.5 py-0.5 rounded bg-blue-50 text-blue-600 font-bold hover:bg-blue-600 hover:text-white transition"
                            >
                              50%
                            </button>
                          </div>
                        )}
                      </div>
                      <input
                        type="number" min={1} max={maxAmt}
                        className="rounded px-2.5 py-1.5 border border-gray-300 text-black w-full text-xs font-bold focus:border-blue-500 focus:outline-none"
                        placeholder="Enter gross amount…"
                        value={editDist.amount}
                        onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); saveEditDist(); } }}
                        onChange={(e) => setEditDist((p) => (p ? { ...p, amount: e.target.value.replace(/^0+/, "") } : p))}
                      />
                      <span className="text-[11px] text-blue-600 font-bold">/ Max Unallocated ₹{maxAmt.toLocaleString("en-IN")}</span>
                    </div>

                    <div className="border border-blue-200 rounded-lg bg-blue-50/50 p-2.5 space-y-2">
                      <div className="text-[11px] font-bold text-blue-900 uppercase tracking-wide">Deductions (Optional)</div>
                      <div className="grid grid-cols-3 gap-2">
                        <div>
                          <label className="block text-[11px] font-semibold text-gray-700 mb-0.5">1. Commission (₹)</label>
                          <input
                            type="number" min={0}
                            className="rounded px-2 py-1 border border-gray-300 text-black text-xs w-full font-medium"
                            placeholder="0"
                            value={editDist.commission}
                            onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); saveEditDist(); } }}
                            onChange={(e) => setEditDist((p) => (p ? { ...p, commission: e.target.value.replace(/^0+/, "") } : p))}
                          />
                        </div>
                        <div>
                          <label className="block text-[11px] font-semibold text-gray-700 mb-0.5">2. TDS (₹)</label>
                          <input
                            type="number" min={0}
                            className="rounded px-2 py-1 border border-gray-300 text-black text-xs w-full font-medium"
                            placeholder="0"
                            value={editDist.tds}
                            onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); saveEditDist(); } }}
                            onChange={(e) => setEditDist((p) => (p ? { ...p, tds: e.target.value.replace(/^0+/, "") } : p))}
                          />
                        </div>
                        <div>
                          <label className="block text-[11px] font-semibold text-gray-700 mb-0.5">3. Collection (₹)</label>
                          <input
                            type="number" min={0}
                            className="rounded px-2 py-1 border border-gray-300 text-black text-xs w-full font-medium"
                            placeholder="0"
                            value={editDist.collection}
                            onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); saveEditDist(); } }}
                            onChange={(e) => setEditDist((p) => (p ? { ...p, collection: e.target.value.replace(/^0+/, "") } : p))}
                          />
                        </div>
                      </div>
                      <div>
                        <label className="block text-[11px] font-semibold text-gray-700 mb-0.5">4. Other Deduction</label>
                        <div className="flex gap-2">
                          <input
                            type="text"
                            className="rounded px-2 py-1 border border-gray-300 text-black text-xs flex-1"
                            placeholder="Deduction Name (e.g. Penalty)"
                            value={editDist.otherDeductionName}
                            onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); saveEditDist(); } }}
                            onChange={(e) => setEditDist((p) => (p ? { ...p, otherDeductionName: e.target.value } : p))}
                          />
                          <input
                            type="number" min={0}
                            className="rounded px-2 py-1 border border-gray-300 text-black text-xs w-28 font-medium"
                            placeholder="Amount (₹)"
                            value={editDist.otherDeduction}
                            onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); saveEditDist(); } }}
                            onChange={(e) => setEditDist((p) => (p ? { ...p, otherDeduction: e.target.value.replace(/^0+/, "") } : p))}
                          />
                        </div>
                      </div>
                    </div>

                    {(() => {
                      const gross = Number(editDist.amount || 0);
                      const comm = Number(editDist.commission || 0);
                      const tds = Number(editDist.tds || 0);
                      const coll = Number(editDist.collection || 0);
                      const oth = Number(editDist.otherDeduction || 0);
                      const totalDed = comm + tds + coll + oth;
                      const netPayable = Math.max(0, gross - totalDed);
                      const exceedsMax = gross > maxAmt;
                      const exceedsDeduction = totalDed > gross;

                      return (
                        <div className="space-y-1.5">
                          {exceedsMax && (
                            <div className="text-[11px] text-red-600 font-bold bg-red-50 p-1.5 rounded border border-red-200">
                              ⚠️ Amount exceeds max unallocated balance (₹{maxAmt.toLocaleString("en-IN")})
                            </div>
                          )}
                          {exceedsDeduction && (
                            <div className="text-[11px] text-red-600 font-bold bg-red-50 p-1.5 rounded border border-red-200">
                              ⚠️ Total deductions cannot exceed gross allocated amount
                            </div>
                          )}
                          <div className="bg-gradient-to-r from-blue-900 to-indigo-900 text-white rounded-lg p-2.5 text-xs space-y-1">
                            <div className="flex justify-between text-blue-100">
                              <span>Gross Allocated Amount:</span>
                              <span className="font-semibold">₹{gross.toLocaleString("en-IN")}</span>
                            </div>
                            {totalDed > 0 && (
                              <div className="text-[11px] space-y-0.5 border-t border-blue-700/60 pt-1 text-red-200">
                                {comm > 0 && <div className="flex justify-between"><span>• Commission:</span><span>- ₹{comm.toLocaleString("en-IN")}</span></div>}
                                {tds > 0 && <div className="flex justify-between"><span>• TDS:</span><span>- ₹{tds.toLocaleString("en-IN")}</span></div>}
                                {coll > 0 && <div className="flex justify-between"><span>• Collection:</span><span>- ₹{coll.toLocaleString("en-IN")}</span></div>}
                                {oth > 0 && <div className="flex justify-between"><span>• {editDist.otherDeductionName.trim() || "Other"}:</span><span>- ₹{oth.toLocaleString("en-IN")}</span></div>}
                              </div>
                            )}
                            <div className="flex justify-between font-extrabold text-xs border-t border-blue-600 pt-1 text-green-300">
                              <span>Net Amount To Be Paid:</span>
                              <span className="text-sm">₹{netPayable.toLocaleString("en-IN")}</span>
                            </div>
                          </div>
                        </div>
                      );
                    })()}
                  </>
                )}
                <div className="flex gap-2 justify-end pt-1">
                  {(() => {
                    const gross = Number(editDist.amount || 0);
                    const comm = Number(editDist.commission || 0);
                    const tds = Number(editDist.tds || 0);
                    const coll = Number(editDist.collection || 0);
                    const oth = Number(editDist.otherDeduction || 0);
                    const isInvalid = !editDist.userType || gross < 1 || gross > maxAmt || (comm + tds + coll + oth) > gross;
                    return (
                      <button
                        className="bg-blue-600 rounded text-white text-xs px-3.5 py-1.5 font-bold hover:bg-black border-2 border-blue-600 disabled:opacity-40 disabled:cursor-not-allowed"
                        disabled={isInvalid}
                        onClick={saveEditDist}
                      >
                        {editDist.editingIdx != null ? "Save" : "Add"}
                      </button>
                    );
                  })()}
                  <button onClick={() => setEditDist(null)} className="bg-black/80 text-white px-3 py-1.5 rounded text-xs hover:bg-blue-600 border border-black font-semibold">Cancel</button>
                </div>
              </div>
            </div>
          );
        })()}
    </div>
  );
};

export default Settlement;